import React, { useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { trackonCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  DocTypeOptions,
  ServiceTypeOptions,
  TypeOfServiceOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Trackon/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

// ... validation schema from above ...
const trackonValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  ServiceType: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Service Type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Service Type is required')
    .required('Service Type is required')
    .default([]),

  TypeOfService: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Type of Service value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Type of Service is required')
    .required('Type of Service is required')
    .default([]),
  DocType: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Doc Type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Doc Type is required')
    .required('Doc Type is required')
    .default([]),
});

const Trackon = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<trackonCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: trackonValidationSchema,
    initialValues: {
      ...carrierSetting.trackon,
      // type: 'D-D',
    },
    onSubmit(values) {
      if (userDataById?._id) {
        if (carrierSetting.trackon._id) {
          dispatch(updateCarrierSettings({ ...carrierSetting.trackon, ...values }));
        } else {
          dispatch(addCarrierSettings({ ...carrierSetting.trackon, ...values }));
        }
      } else {
        dispatch(setCarrierSettings({ values, carrierType: 'trackon' }));
      }
      onClose();
    },
  });

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Trackon Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`Trackon Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo?.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="DocType"
                    label={`Doc Type`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="DocType"
                    value={values?.DocType?.map(item => item.value)}
                    options={DocTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('DocType', selectedObj);
                    }}
                    error={errors.DocType as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="TypeOfService"
                    label={`Type of Service`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="TypeOfService"
                    value={values?.TypeOfService?.map(item => item.value)}
                    options={TypeOfServiceOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('TypeOfService', selectedObj);
                    }}
                    error={errors.TypeOfService as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="ServiceType"
                    label={`Service Type`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="ServiceType"
                    value={values?.ServiceType?.map(item => item.value)}
                    options={ServiceTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('ServiceType', selectedObj);
                    }}
                    error={errors.ServiceType as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default Trackon;
